import type { Recipe } from '../data/types';
import { scaleAmount } from './scale';
import packages from '../../pantry/packages.json';

// Turns one recipe — or a whole week of them — into the messages you paste
// into Walmart's Sparky (the smiley in the app's bottom bar) to build a cart.
//
// The pipeline:
//   cartLines(items)      every ingredient across every recipe, merged by id,
//                         scaled per recipe, sized to real packages
//   sparkyMessages…       cut into numbered batches under SPARKY_MAX chars
//   leftoversFor(items)   perishables the plan uses well under a package of
//   pairings(items, all)  recipes NOT in the plan that would use those up
//
// Package sizes and classes come from the pantry state the kitchen-ops skill
// keeps (pantry/packages.json), keyed by ingredient id. A tracked staple is
// listed as one package with the need in parentheses ("2.5 oz jar (need 1
// tbsp)"); fresh things get their real Walmart package (dozen eggs, head of
// garlic); bulk (Liam's freezer) and ignore (water) never make the list.

// Not documented by Walmart. Batches of ~430 chars have gone through cleanly;
// bump this down if Sparky starts truncating.
export const SPARKY_MAX = 450;

export type CartItem = { recipe: Recipe; factor: number };

type Pkg = {
  class?: string;
  package?: string;
  keeps_days?: number;
  freeze?: string;
  quick?: string[];
  grams?: number;
  count?: number;
  cans?: number;
  packets?: number;
  cups_yield?: number;
};
const DATA = packages as unknown as {
  items: Record<string, Pkg>;
  densities_g_per_tsp: Record<string, number>;
  densities_g_per_cup: Record<string, number>;
};
const PKG = DATA.items;
export function packageInfo(id: string): Pkg | undefined {
  return PKG[id];
}

const PACKAGE_UNIT = /\b(cans?|jars?|bags?|boxes|box|cartons?|packages?|packets?|containers?|bottles?|blocks?|loaf|loaves|dozen)\b/i;
const RATE = /\b(per|each|apiece)\b/i;
const NEVER_BUY = /^(water|ice|hot water|tap water)$/i;

// ---- how much of a package does an amount use? ------------------------
//
// "1 tbsp cumin" is a jar. "4½ tbsp cumin" (tripled) is still one jar, but
// "12 bay leaves" (quadrupled) is one 14-leaf jar with nothing to spare and
// "6 cups lentils" is two bags. The pantry data carries what each package
// holds (grams / count / cans / cups it yields) and the densities to turn
// spoons and cups into grams, so the count is arithmetic, not a guess.

const UNI: Record<string, number> = { '½': 0.5, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 0.25, '¾': 0.75, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875 };
const NUM = /(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?\s*[½⅓⅔¼¾⅛⅜⅝⅞]|[½⅓⅔¼¾⅛⅜⅝⅞]|\d+(?:\.\d+)?)\s*([a-zA-Z]+)?/g;

function num(t: string): number {
  const u = t.match(/^(\d+(?:\.\d+)?)?\s*([½⅓⅔¼¾⅛⅜⅝⅞])$/);
  if (u) return (u[1] ? parseFloat(u[1]) : 0) + UNI[u[2]];
  const mixed = t.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) return +mixed[1] + +mixed[2] / +mixed[3];
  const frac = t.match(/^(\d+)\/(\d+)$/);
  if (frac) return +frac[1] / +frac[2];
  return parseFloat(t);
}

type Need = { kind: 'g' | 'count' | 'cups' | 'cans' | 'packets'; value: number } | null;

// Sum every "<number> <unit>" in the amount ("1 cup + 1 tbsp") into one need.
function parseNeed(id: string, amount: string): Need {
  const clean = amount.replace(/\([^)]*\)/g, '');
  let g = 0, count = 0, cups = 0, cans = 0, packets = 0, any = false;
  const gPerTsp = DATA.densities_g_per_tsp[id];
  const gPerCup = DATA.densities_g_per_cup[id] ?? (gPerTsp ? gPerTsp * 48 : undefined);
  for (const m of clean.matchAll(NUM)) {
    const v = num(m[1]);
    const u = (m[2] ?? '').toLowerCase();
    any = true;
    if (/^(tsp|teaspoons?|t)$/.test(u)) g += v * (gPerTsp ?? 4);
    else if (/^(tbsp|tablespoons?|tbs)$/.test(u)) g += v * 3 * (gPerTsp ?? 4.5);
    else if (/^(cups?|c)$/.test(u)) { cups += v; g += v * (gPerCup ?? 200); }
    else if (/^(oz|ounces?)$/.test(u)) g += v * 28.35;
    else if (/^(lb|lbs|pounds?)$/.test(u)) g += v * 453.6;
    else if (/^(cans?)$/.test(u)) cans += v;
    else if (/^(packets?|packs?)$/.test(u)) packets += v;
    else if (/^(pints?)$/.test(u)) { cups += v * 2; g += v * 2 * (gPerCup ?? 240); }
    else if (/^(quarts?|qt)$/.test(u)) { cups += v * 4; g += v * 4 * (gPerCup ?? 240); }
    else count += v; // bare number or a countable word: leaves, cloves, eggs
  }
  if (!any) return null;
  if (cans) return { kind: 'cans', value: cans };
  if (packets) return { kind: 'packets', value: packets };
  if (g) return { kind: 'g', value: g };
  if (cups) return { kind: 'cups', value: cups };
  if (count) return { kind: 'count', value: count };
  return null;
}

// Fraction of ONE package the amount uses, or null when the data can't say.
export function packageFraction(id: string, amount: string, pkg: Pkg | undefined = PKG[id]): number | null {
  if (!pkg) return null;
  const need = parseNeed(id, amount);
  if (!need) return null;
  if (need.kind === 'cans' && pkg.cans) return need.value / pkg.cans;
  if (need.kind === 'packets' && pkg.packets) return need.value / pkg.packets;
  if (need.kind === 'count' && pkg.count) return need.value / pkg.count;
  if (need.kind === 'cups' && pkg.cups_yield) return need.value / pkg.cups_yield;
  if (need.kind === 'g' && pkg.grams) return need.value / pkg.grams;
  if (need.kind === 'cups' && pkg.grams) return (need.value * 200) / pkg.grams;
  if (need.kind === 'count' && pkg.grams && pkg.count) return need.value / pkg.count;
  return null;
}

function ceilPackages(fraction: number): number {
  return Math.max(1, Math.ceil(fraction - 1e-9));
}

// ---- merging ------------------------------------------------------------

export type CartLine = {
  id: string;
  name: string;
  optional: boolean;             // optional in EVERY recipe that uses it
  parts: { title: string; qty: string }[];
  packages: number | null;       // how many to buy, when the data can say
  fraction: number | null;       // Σ of package fractions across the plan
  pkg?: Pkg;
  text: string;                  // the line as Sparky should read it
};

// Every bought ingredient across the plan, merged by ingredient id. The
// same id in two recipes becomes one line whose need is the sum — two
// recipes wanting ½ pint of cream each buy one pint, not two.
export function cartLines(items: CartItem[]): CartLine[] {
  const byId = new Map<string, CartLine>();
  for (const { recipe, factor } of items) {
    for (const ing of recipe.ingredients) {
      if (ing.fromFreezer || ing.shop === false) continue;
      const pkg = PKG[ing.id];
      if (!ing.shop && (NEVER_BUY.test(ing.name.trim()) || pkg?.class === 'bulk' || pkg?.class === 'ignore')) continue;
      const qty = ing.shop ? ing.shop : scaleAmount(ing.total, factor).text.trim();
      const line = byId.get(ing.id) ?? {
        id: ing.id,
        name: ing.name,
        optional: true,
        parts: [],
        packages: null,
        fraction: null,
        pkg,
        text: '',
      };
      line.optional = line.optional && !!ing.optional;
      line.parts.push({ title: recipe.title, qty });
      byId.set(ing.id, line);
    }
  }

  const lines = [...byId.values()];
  for (const line of lines) {
    const pkg = line.pkg;
    const custom = line.parts.some((p) => p.qty === (items.find((i) => i.recipe.title === p.title)?.recipe.ingredients.find((x) => x.id === line.id)?.shop));
    const usePkg = !!pkg?.package && !custom && !/\band\b/i.test(line.name)
      && !line.parts.some((p) => PACKAGE_UNIT.test(p.qty));
    if (usePkg) {
      let total = 0;
      let resolvable = true;
      for (const p of line.parts) {
        if (/^to taste$/i.test(p.qty) || RATE.test(p.qty)) continue; // counts as "a package"
        const f = packageFraction(line.id, p.qty, pkg);
        if (f === null) { resolvable = false; break; }
        total += f;
      }
      line.fraction = resolvable ? total : null;
      line.packages = resolvable ? ceilPackages(total) : 1;
      line.text = packageText(line);
    } else {
      line.text = plainText(line);
    }
    if (line.optional) line.text += ' (optional — skip if pricey)';
  }

  // required first, optional last, so a cut-off list loses the least
  return [...lines.filter((l) => !l.optional), ...lines.filter((l) => l.optional)];
}

function needText(line: CartLine): string {
  const parts = line.parts.filter((p) => !/^to taste$/i.test(p.qty));
  if (!parts.length) return '';
  if (parts.length === 1) return RATE.test(parts[0].qty) ? ` (${parts[0].qty})` : ` (need ${parts[0].qty})`;
  // Past 3 recipes the per-recipe breakdown stops helping and starts blowing
  // past Sparky's message cap (salt in 12 recipes was 417 chars on its own).
  if (parts.length > 3) return ` (used in ${parts.length} recipes)`;
  return ` (need ${parts.map((p) => `${p.qty} for ${shortTitle(p.title)}`).join(' + ')})`;
}

function shortTitle(t: string): string {
  return t.split(/\s+[—–-]\s+|\s+&\s+/)[0].split(' ').slice(0, 3).join(' ');
}

// One cart line from a package definition. Reads the way a person would
// type it to Sparky: "Garlic, 3 × head of garlic (need 24 cloves)",
// "Zucchini, 4", "8 oz BelGioioso Parmesan wedge (need 1 cup)".
function packageText(line: CartLine): string {
  const pkg = line.pkg!;
  const size = pkg.package!;
  const n = line.packages ?? 1;
  const need = needText(line);

  // Single loose items (a lime, a zucchini): the count is the whole story.
  if (pkg.count === 1 && !pkg.grams && !/^1\s/.test(size)) return `${line.name}, ${n}`;

  // Package text that already names the product ("8 oz BelGioioso Parmesan
  // wedge") stands alone; otherwise lead with the ingredient name.
  const named = size.toLowerCase().includes(line.name.toLowerCase().replace(/^(fresh|large|whole)\s+/, ''));
  const lead = named ? '' : `${line.name}, `;
  const sized = /^\d/.test(size);
  const how = n === 1 ? (sized ? size : `1 ${size}`) : `${n} × ${size.replace(/^1\s+/, '')}`;
  return `${lead}${how}${need}`;
}

function plainText(line: CartLine): string {
  const real = line.parts.filter((p) => !/^to taste$/i.test(p.qty));
  const qtys = real.length ? real.map((p) => p.qty) : ['small amount'];
  return `${line.name}, ${qtys.join(' + ')}`;
}

// ---- Sparky messages ----------------------------------------------------

const PREAMBLE =
  "Build a Walmart delivery cart. Don't check out. Great Value by default; organic if the price is close. Skip anything with junk fillers. Add everything even if I might have it.";

export function sparkyMessagesFor(items: CartItem[], max = SPARKY_MAX): string[] {
  const lines = cartLines(items).map((l, i) => `${i + 1} ${l.text}`);
  if (!lines.length) return [];
  const title = items.length === 1 ? items[0].recipe.title : `${items.length} recipes this week`;

  // Chunk greedily to learn how many batches there are, then rebuild with
  // the real "k of N" headers (the header length changes the fit, so loop
  // until the count settles).
  let n = 1;
  for (let attempt = 0; attempt < 6; attempt++) {
    const msgs = chunk(lines, title, n, max);
    if (msgs.length === n) return msgs;
    n = msgs.length;
  }
  return chunk(lines, title, n, max);
}

export function sparkyMessages(recipe: Recipe, factor = 1, max = SPARKY_MAX): string[] {
  return sparkyMessagesFor([{ recipe, factor }], max);
}

function header(title: string, k: number, n: number): string {
  if (n === 1) return `${PREAMBLE} This is for ${title}.`;
  if (k === 1) return `${PREAMBLE} I'll send items in ${n} messages for ${title} — add each batch to the same cart.`;
  return `Add to the same cart (batch ${k} of ${n}):`;
}

function chunk(lines: string[], title: string, n: number, max: number): string[] {
  const msgs: string[] = [];
  let k = 1;
  let cur = header(title, k, n);
  for (const line of lines) {
    const next = `${cur}\n${line}`;
    if (next.length > max && cur !== header(title, k, n)) {
      msgs.push(cur);
      k++;
      cur = `${header(title, k, n)}\n${line}`;
    } else {
      cur = next;
    }
  }
  msgs.push(cur);
  if (n > 1 || msgs.length > 1) msgs[msgs.length - 1] += "\nThat's everything.";
  return msgs;
}

// ---- what will be left over ------------------------------------------
//
// Buying a pint of cream for ½ cup of soup is how cream goes off in the
// back of the fridge. For every perishable line that uses well under a
// package, say how much is left, how long it keeps, whether it freezes and
// how, a quick way to use it, and which other recipes in the book use it —
// so the next cook can be picked to use it up.

export type Leftover = {
  id: string;
  name: string;
  pkg: string;
  leftPct: number;      // 0–100, of the LAST package bought
  keepsDays?: number;
  freeze?: string;
  quick?: string[];
  alsoIn: { slug: string; title: string }[];
};

export function leftoversFor(items: CartItem[], all: Recipe[] = []): Leftover[] {
  const inPlan = new Set(items.map((i) => i.recipe.slug));
  const out: Leftover[] = [];
  for (const line of cartLines(items)) {
    const pkg = line.pkg;
    if (!pkg?.package || pkg.class !== 'fresh' || line.fraction === null) continue;
    const n = ceilPackages(line.fraction);
    const leftPct = Math.round((1 - (line.fraction - (n - 1))) * 100);
    if (leftPct < 40) continue; // most of it gets used; nothing to plan around
    const alsoIn = all
      .filter((r) => !inPlan.has(r.slug) && r.ingredients.some((i) => i.id === line.id && !i.fromFreezer))
      .map((r) => ({ slug: r.slug, title: r.title }));
    out.push({
      id: line.id,
      name: line.name,
      pkg: pkg.package.replace(/^1\s+/, ''),
      leftPct,
      keepsDays: pkg.keeps_days,
      freeze: pkg.freeze,
      quick: pkg.quick,
      alsoIn,
    });
  }
  // shortest shelf life first — that's what needs a plan
  return out.sort((a, b) => (a.keepsDays ?? 999) - (b.keepsDays ?? 999));
}

export function leftovers(recipe: Recipe, factor = 1, all: Recipe[] = []): Leftover[] {
  return leftoversFor([{ recipe, factor }], all);
}

// ---- pairings -----------------------------------------------------------
//
// Recipes not yet in the plan, ranked by how much of the plan's leftovers
// they would use. A recipe that takes the half bag of spinach that goes off
// in five days outranks one that takes some of the onions.

export type Pairing = { recipe: Recipe; score: number; usesUp: string[] };

export function pairings(items: CartItem[], all: Recipe[], limit = 3): Pairing[] {
  const left = leftoversFor(items, all);
  if (!left.length) return [];
  const inPlan = new Set(items.map((i) => i.recipe.slug));
  const out: Pairing[] = [];
  for (const r of all) {
    if (inPlan.has(r.slug)) continue;
    let score = 0;
    const usesUp: string[] = [];
    for (const l of left) {
      const ing = r.ingredients.find((i) => i.id === l.id && !i.fromFreezer);
      if (!ing) continue;
      // urgency × amount left: short keepers count more
      const urgency = l.keepsDays ? Math.max(0.3, 1 - l.keepsDays / 60) : 0.5;
      score += (l.leftPct / 100) * urgency + (ing.optional ? 0 : 0.2);
      usesUp.push(l.name);
    }
    if (usesUp.length) out.push({ recipe: r, score, usesUp });
  }
  return out.sort((a, b) => b.score - a.score).slice(0, limit);
}
